import React, { useState } from "react";
import { ProactiveAuditItem } from "../../../models/weeklyTimeline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/free-regular-svg-icons";
import { faCloudArrowDown, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { getTimezoneTime, downloadFile } from "./utils";
import ProactiveAuditDetailsModal from "../ProactiveAuditDetailsModal";

interface ProactiveAuditItemProps {
  item: ProactiveAuditItem;
  timezone: string;
  bottomLastMargin: string;
  verticalLineStyle: string;
}

const ProactiveAuditItemComponent: React.FC<ProactiveAuditItemProps> = ({
  item,
  timezone,
  bottomLastMargin,
  verticalLineStyle,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const noteContent = item.audit.issueDetails || "No Note";
  const shortNote =
    noteContent.length > 90
      ? `${noteContent.substring(0, 87)}...`
      : noteContent;

  return (
    <>
      <div 
        className={`mt-1 flex flex-col ${bottomLastMargin} cursor-pointer hover:bg-gray-50 rounded-md p-2 -m-2 transition-colors`}
        onClick={() => setIsModalOpen(true)}
      >
        <div className="flex flex-row">
          <div className="flex h-[30px] w-[30px] min-w-[30px] items-center justify-center rounded-full bg-[#af52de]">
            <FontAwesomeIcon className="h-4 w-4 text-white" icon={faPenToSquare} />
          </div>
          <h1 className="ml-3 flex items-center justify-center text-sm text-[#212322]">
            Performed Proactive Audit
          </h1>
        </div>
      <div className="mt-1 flex flex-row items-stretch">
        <div className={`ml-[0.7rem] ${verticalLineStyle}`} />
        <div className="ml-6 flex w-full flex-col space-y-2">
          <span className="text-xs font-light text-gray-500">
            on part{" "}
            <strong className="font-semibold">
              #{item.audit.part.number}
            </strong>{" "}
            @ {getTimezoneTime(item.time, timezone)} {timezone}
          </span>
          <span>
            {item.audit.issues ? (
              <div className="flex h-full w-[30%] flex-row items-center justify-center rounded-md border-[1px] border-[#ff453a] bg-red-100 px-1">
                <h3 className="ml-1 pt-[0.05rem] text-[0.6rem] text-[#ff453a] whitespace-nowrap">
                  Issue Found
                </h3>
              </div>
            ) : (
              <div className="flex h-full w-[40%] flex-row items-center justify-center rounded-md border-[1px] border-[#30d158] bg-green-100 px-1">
                <h3 className="ml-1 pt-[0.05rem] text-[0.6rem] text-[#30d158] whitespace-nowrap">
                  No Issue Found
                </h3>
              </div>
            )}
          </span>
          {item.audit.attachments.length > 0 && (
            <div className="w-full rounded-lg border-[1px] border-gray-300 py-1">
              <div>
                {item.audit.attachments.map((attachment, i) => {
                  const shortFileName =
                    attachment.attachment.filename.length > 35
                      ? `${attachment.attachment.filename.substring(0, 32)}...`
                      : attachment.attachment.filename;
                  return (
                    <div
                      key={i}
                      className="mb-1 flex w-full flex-row items-center justify-between border-b-[1px] px-1 pb-1 cursor-pointer hover:bg-gray-50"
                      style={{ cursor: 'pointer' }}
                      onClick={() => downloadFile(
                        attachment.attachment.downloadURL,
                        attachment.attachment.filename,
                      )}
                    >
                      <FontAwesomeIcon className="h-[12px] w-[12px] text-gray-600 mr-[4px]" icon={faFile} />
                      <span className="text-xs font-light text-gray-600 underline">
                        {shortFileName}
                      </span>
                      <FontAwesomeIcon className="h-[12px] w-[12px] text-blue-600 ml-[4px]" icon={faCloudArrowDown} />
                    </div>
                  );
                })}
              </div>
              <div className="max-w-[295px] break-words px-1 text-xs font-light">
                {isExpanded ? noteContent : shortNote}
                {noteContent.length > 90 && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="ml-1 text-blue-600 hover:underline"
                  >
                    {isExpanded ? "Read Less" : "Read More"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>

      <ProactiveAuditDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        auditItem={item}
        timezone={timezone}
      />
    </>
  );
};

export default ProactiveAuditItemComponent; 